/******************************************************************************
 * Memokid: A game to train one's memory                                      *
 * Copyright (C) 2022 Lukas Laag                                              *
 * This file is part of Memokid                                               *
 *                                                                            *
 * Memokid is free software: you can redistribute it and/or modify            *
 * it under the terms of the GNU General Public License as published by       *
 * the Free Software Foundation, either version 3 of the License, or          *
 * (at your option) any later version.                                        *
 *                                                                            *
 * Memokid is distributed in the hope that it will be useful,                 *
 * but WITHOUT ANY WARRANTY; without even the implied warranty of             *
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the              *
 * GNU General Public License for more details.                               *
 *                                                                            *
 * You should have received a copy of the GNU General Public License          *
 * along with Memokid.  If not, see <https://www.gnu.org/licenses/>.          *
 *                                                                            *
 ******************************************************************************/

import { createContext, useReducer } from 'react'
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl'
import en from './translations/memokid_en'
import fr from './translations/memokid_fr'
import { US as UsFlag, FR as FrFlag } from 'country-flag-icons/react/3x2'

const { localStorage } = window
const LANGUAGE_KEY = 'memokid.lang'

export const translations = {
  en: {
    catalog: en,
    flag: <UsFlag />,
    translator: 'Lukaas Laag'
  },
  fr: {
    catalog: fr,
    flag: <FrFlag />,
    translator: 'Anne-Marie Doué'
  }
}

export const getLocale = () => {
  let locale = localStorage.getItem(LANGUAGE_KEY)
  if (!locale) {
    if (navigator) {
      const { languages } = navigator
      if (languages) {
        locale = languages.find(language => Object.prototype.hasOwnProperty.call(translations, language))
        if (!locale) {
          locale = 'en'
        }
      }
    }
  }
  return locale
}

const updateTranslations = locale => {
  const regionNames = new Intl.DisplayNames([locale], { type: 'language' })
  Object.entries(translations).forEach(([locale, translation]) => {
    translation.language = regionNames.of(locale)
  })
}

export const createProvider = locale => {
  localStorage.setItem(LANGUAGE_KEY, locale)
  const cache = createIntlCache()
  updateTranslations(locale)
  return createIntl(translations[locale].catalog, cache)
}

export const nls = {
  intl: createProvider(getLocale())
}
export default nls

export const UPDATE = 'UPDATE'

const localeReducer = (state, action) => {
  const { type } = action
  switch (type) {
    case UPDATE: {
      const { locale } = action
      nls.intl = createProvider(locale)
      return nls.intl
    }
    default: {
      throw new Error(`Unknown action ${type}`)
    }
  }
}

export const NlsContext = createContext(null)

export const ContextualIntlProvider = props => {
  const { children } = props
  const [intl, dispatch] = useReducer(localeReducer, nls.intl)
  return (
    <NlsContext.Provider value={dispatch}>
      <RawIntlProvider value={intl}>
        {children}
      </RawIntlProvider>
    </NlsContext.Provider>
  )
}
